import React, { useEffect, useRef, useState } from 'react';
import { get } from '../../utils/ajax';
import { remoteRoutes } from '../../data/constants';
import { colors } from '../../theme/tokens';
import Toast from '../../utils/Toast';

// Google's multicolour "G" mark — fixed brand colours required by Google's
// branding guidelines, so these are intentionally not design tokens.
const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"
    />
    <path
      fill="#FBBC05"
      d="M10.5 28.7a14.5 14.5 0 0 1 0-9.4l-7.9-6.1a24 24 0 0 0 0 21.6l7.9-6.1z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.9 2.3-8.4 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z"
    />
  </svg>
);

// Shared with the primary button (see `submit` in loginStyles).
const PILL_HEIGHT = 44;

/**
 * Master switch for the auth pages. Even when true the button only renders
 * if the server reports a configured client ID, so turning this on cannot
 * surface a button that would fail.
 */
export const GOOGLE_SIGN_IN_ENABLED = true;

const GSI_SRC = 'https://accounts.google.com/gsi/client';

// The browser needs the client ID before Google's script can be initialised,
// and both the login and register screens ask for it. Cached at module level
// so moving between those screens doesn't re-request it.
let configPromise: Promise<{
  enabled: boolean;
  clientId: string | null;
}> | null = null;

const loadConfig = () => {
  if (!configPromise) {
    configPromise = new Promise((resolve) => {
      get(
        remoteRoutes.googleConfig,
        (resp) =>
          resolve({
            enabled: !!resp?.enabled,
            clientId: resp?.clientId ?? null,
          }),
        // If this fails we simply don't offer Google — the password form
        // still works, so there's nothing to show the user here.
        () => resolve({ enabled: false, clientId: null }),
      );
    });
  }
  return configPromise;
};

let scriptPromise: Promise<boolean> | null = null;

const loadScript = () => {
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve) => {
      if ((window as any).google?.accounts?.id) {
        resolve(true);
        return;
      }
      const existing = document.querySelector<HTMLScriptElement>(
        `script[src="${GSI_SRC}"]`,
      );
      if (existing) {
        existing.addEventListener('load', () => resolve(true));
        existing.addEventListener('error', () => resolve(false));
        return;
      }
      const script = document.createElement('script');
      script.src = GSI_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

interface IProps {
  /** Receives the Google ID token — verify it server-side, never trust it here. */
  onCredential: (credential: string) => void;
  text?: 'signin_with' | 'signup_with' | 'continue_with';
  disabled?: boolean;
  /** Where the button sits relative to the 'or' divider. */
  placement?: 'top' | 'bottom';
  /** Show the 'or' divider (the light design omits it). */
  divider?: boolean;
}

const GoogleButton = ({
  onCredential,
  text = 'continue_with',
  disabled,
  placement = 'bottom',
  divider: showDivider = true,
}: IProps) => {
  const holder = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  // Google calls back into whatever callback was registered at init time, so
  // keep the latest handler in a ref rather than re-initialising on each render.
  const handler = useRef(onCredential);
  handler.current = onCredential;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const config = await loadConfig();
      if (cancelled) return;
      if (!config.enabled || !config.clientId) {
        setUnavailable(true);
        return;
      }

      const loaded = await loadScript();
      if (cancelled) return;
      const google = (window as any).google;
      if (!loaded || !google?.accounts?.id || !holder.current) {
        setUnavailable(true);
        return;
      }

      google.accounts.id.initialize({
        client_id: config.clientId,
        callback: (resp: any) => {
          if (resp?.credential) handler.current(resp.credential);
        },
      });
      google.accounts.id.renderButton(holder.current, {
        theme: 'outline',
        size: 'large',
        shape: 'pill',
        text,
        width: holder.current.offsetWidth || 320,
        logo_alignment: 'center',
      });
      setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [text]);

  const divider = showDivider ? (
    <div
      style={{
        margin: placement === 'top' ? '16px 0 4px' : '4px 0 14px',
        textAlign: 'center',
        color: colors.textPrimary,
        fontSize: 14,
        fontWeight: 500,
      }}
    >
      Or
    </div>
  ) : null;

  // Same size/shape as the primary "Sign In" button so the two read as a pair.
  const pillStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    boxSizing: 'border-box',
    width: '100%',
    height: PILL_HEIGHT,
    borderRadius: 999,
    border: `1px solid ${colors.authOutlineBorder}`,
    background: 'transparent',
    color: colors.textPrimary,
    font: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    opacity: disabled ? 0.5 : 1,
  };

  const label = (
    <>
      <GoogleLogo />
      Continue with Google
    </>
  );

  // The visible button is always our own so it matches the design. When Google
  // sign-in is configured, Google's real button sits on top of it, invisible,
  // and receives the click; when it isn't, clicking just explains why.
  // Swap the placeholder's onClick for the real flow when the backend is ready.
  const button = (
    <div style={{ position: 'relative', height: PILL_HEIGHT }}>
      {unavailable ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => Toast.info('Google sign-in is coming soon')}
          style={{ ...pillStyle, cursor: disabled ? 'default' : 'pointer' }}
        >
          {label}
        </button>
      ) : (
        <>
          <div style={{ ...pillStyle, pointerEvents: 'none' }} aria-hidden>
            {label}
          </div>
          <div
            ref={holder}
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              opacity: 0.01,
              transform: 'scaleY(1.1)',
              pointerEvents: disabled ? 'none' : 'auto',
            }}
          />
        </>
      )}
    </div>
  );

  return (
    <div style={{ marginTop: placement === 'top' ? 0 : showDivider ? 16 : 12 }}>
      {placement === 'bottom' && divider}
      {button}
      {placement === 'top' && divider}
    </div>
  );
};

export default GoogleButton;
